import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { RequestStatus } from "@/pages/YourData";
import { api, formatError, fmtDate } from "@/lib/api";

const EraseButton = ({ r, onDone }) => {
  const erase = async () => {
    try {
      const { data } = await api.post(`/admin/data-requests/${r.id}/erase`);
      toast.success(data.resolution_note);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-dr-erase-${r.id.slice(0, 8)}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-500"><Trash2 size={13} /> Erase account & letters</button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="erase-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Erase {r.user_name}'s account?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the account, wipes every letter they sent (files and recipient details), cancels any unclaimed letters and unlinks letters they received.
            Payment records and the audit trail are kept. The user is emailed and the request is marked completed. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="erase-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="erase-confirm-btn" onClick={erase} className="bg-red-600 hover:bg-red-500">Erase permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const Resolve = ({ r, onDone }) => {
  const [note, setNote] = useState("");
  const act = async (status) => {
    try {
      await api.patch(`/admin/data-requests/${r.id}`, { status, note });
      toast.success(`Request ${status}`);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center">
      {r.type === "erasure" && <EraseButton r={r} onDone={onDone} />}
      <Input data-testid={`admin-dr-note-${r.id.slice(0, 8)}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to user (optional)" className="h-9 w-56" />
      <button data-testid={`admin-dr-complete-${r.id.slice(0, 8)}`} onClick={() => act("completed")} className="px-3 h-9 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500">Mark completed</button>
      <button data-testid={`admin-dr-reject-${r.id.slice(0, 8)}`} onClick={() => act("rejected")} className="px-3 h-9 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">Reject</button>
    </div>
  );
};

export const AdminDataRequests = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/data-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="admin-data-requests">
      <p className="text-sm text-slate-500">UK GDPR requests must be answered within one month. Deletion is carried out manually; payment records and the audit trail may need to be retained or restricted.</p>
      {!rows.length && <p className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-xl">No data requests.</p>}
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-dr-${r.id.slice(0, 8)}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-wrap justify-between gap-4">
          <div className="text-sm">
            <p className="font-semibold">{r.type === "access" ? "Subject access (copy)" : "Erasure (deletion)"} <span className="font-mono text-xs text-slate-400">#{r.id.slice(0, 8).toUpperCase()}</span> <RequestStatus s={r.status} /></p>
            <p className="text-slate-500 mt-1">{r.user_name} · {r.user_email}</p>
            <p className="text-xs text-slate-500 mt-1 font-mono">submitted {fmtDate(r.created_at)} · due {fmtDate(r.due_at)}{r.resolved_at ? ` · resolved ${fmtDate(r.resolved_at)}` : ""}</p>
            {r.details && <p className="mt-2 max-w-xl">{r.details}</p>}
            {r.resolution_note && <p className="mt-1 text-xs text-slate-500">Note: {r.resolution_note}</p>}
          </div>
          {r.status === "pending" && <Resolve r={r} onDone={load} />}
        </div>
      ))}
    </div>
  );
};
